import type { WorkflowInvitation } from "@/lib/data/requests/connections/workflow-invitations";
import type { Connection, MatterContactConnection } from "@/lib/types";
import { getConnectionPreviewContact } from "./item-contact-connections";

const isCurrentContactConnection = (connection: Connection) =>
  connection.targetObjectType === "contact" &&
  (connection.state === "active" || connection.state === "scheduled");

export const withSignatureRequestInvitations = <T extends { id: string }>(
  connections: T[],
  invitations: WorkflowInvitation[] = [],
) =>
  connections.map((connection) => ({
    ...connection,
    signatureRequestPasscode:
      invitations.find((item) => item.connectionId === connection.id)
        ?.passcode ?? "",
  }));

export const getWorkflowContactConnections = (
  connections: Connection[],
  sourceObjectType: "matter_share_folder" | "matter_signature_request",
) =>
  connections.filter(
    (connection) =>
      connection.sourceObjectType === sourceObjectType &&
      isCurrentContactConnection(connection),
  );

export const toWorkflowContactConnectionPresentation = ({
  access,
  connection,
  label,
  matterId,
}: {
  access?: MatterContactConnection;
  connection: Connection;
  label: "Accessor" | "Signer";
  matterId: string;
}): MatterContactConnection => {
  const accessRecord = access as unknown as Record<string, unknown> | undefined;
  return {
    ...access,
    ...connection,
    contact: getConnectionPreviewContact(connection),
    contactId: connection.targetObjectId,
    firmId: access?.firmId ?? "",
    label,
    matterId,
    pendingSigner: connection.pending ?? accessRecord?.pendingSigner,
    signatureRequestComplete:
      connection.signatureRequestComplete ?? access?.signatureRequestComplete,
    signerOrder:
      connection.signerOrder ??
      accessRecord?.signerOrder ??
      accessRecord?.matterSignatureRequestSignerOrder,
  } as unknown as MatterContactConnection;
};

export const buildWorkflowContactConnectionPresentations = ({
  invitations = [],
  connections,
  label,
  matterId,
  sourceObjectType,
}: {
  accessConnections?: MatterContactConnection[];
  invitations?: WorkflowInvitation[];
  connections: Connection[];
  label: "Accessor" | "Signer";
  matterId: string;
  sourceObjectType: "matter_share_folder" | "matter_signature_request";
}) =>
  getWorkflowContactConnections(connections, sourceObjectType).map(
    (connection) => ({
      ...toWorkflowContactConnectionPresentation({
        connection,
        label,
        matterId,
      }),
      ...(label === "Signer"
        ? {
            signatureRequestPasscode:
              invitations.find((item) => item.connectionId === connection.id)
                ?.passcode ?? "",
          }
        : {
            shareFolderPasscode:
              invitations.find((item) => item.connectionId === connection.id)
                ?.passcode ?? "",
          }),
    }),
  );
